import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../api/api';
import styles from './PptxEditor.module.css';
import {
    X, Save, Download, Mail, ChevronLeft, ChevronRight, LayoutTemplate, Info,
} from 'lucide-react';

const PptxEditor = ({ fileUrl, fileName, pptxBlobName, onClose }) => {

    // --- ÉTAT ---
    const [slides, setSlides] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);

    // --- MISE À L'ÉCHELLE RESPONSIVE DE LA DIAPOSITIVE ---
    const stageRef = useRef(null);
    const [scale, setScale] = useState(1);

    // --- CHARGEMENT AUTOMATIQUE PPTX ---
    const loadPptxData = useCallback(async () => {
        if (!pptxBlobName) return;
        setLoading(true);
        try {
            const response = await api.post('/pptx/parse', { blob_name: pptxBlobName });
            const slidesWithOriginals = response.data.slides.map(slide => ({
                ...slide,
                shapes: slide.shapes.map(shape => ({ ...shape, originalText: shape.text }))
            }));
            setSlides(slidesWithOriginals);
            setMeta(response.data.meta);
        } catch (error) {
            console.error("Erreur chargement PPTX", error);
            alert("Erreur lors du chargement de l'édition.");
        } finally {
            setLoading(false);
        }
    }, [pptxBlobName]);

    useEffect(() => {
        loadPptxData();
    }, [loadPptxData]);

    // Recalcule le zoom de la diapositive pour qu'elle tienne toujours
    // dans l'espace disponible (écran desktop, tablette ou mobile).
    useLayoutEffect(() => {
        if (!meta) return;

        const computeScale = () => {
            const stageEl = stageRef.current;
            if (!stageEl) return;

            const isMobile = window.innerWidth <= 768;
            const horizontalPadding = isMobile ? 24 : 64;
            const verticalPadding = isMobile ? 24 : 64;

            // Réserve la place utilisée sous la diapositive par le nav pill
            // et la bande de miniatures, pour que le zoom calculé ne les
            // fasse pas déborder de l'écran (surtout en mobile portrait).
            const controlsReserve = (isMobile ? 44 : 48)
                + (slides.length > 1 ? (isMobile ? 46 : 52) : 0)
                + (isMobile ? 14 : 20); // gaps entre les blocs

            const availableWidth = stageEl.clientWidth - horizontalPadding;
            const availableHeight = stageEl.clientHeight - verticalPadding - controlsReserve;

            const widthRatio = availableWidth / meta.width;
            const heightRatio = availableHeight / meta.height;

            // On ne dépasse jamais 100% pour rester fidèle aux dimensions réelles.
            const nextScale = Math.min(widthRatio, heightRatio, 1);
            setScale(nextScale > 0 ? nextScale : 0.1);
        };

        computeScale();

        const stageEl = stageRef.current;
        const resizeObserver = new ResizeObserver(computeScale);
        if (stageEl) resizeObserver.observe(stageEl);
        window.addEventListener('resize', computeScale);
        window.addEventListener('orientationchange', computeScale);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', computeScale);
            window.removeEventListener('orientationchange', computeScale);
        };
    }, [meta, slides.length]);

    // --- FONCTIONS D'ÉDITION ---
    const handleTextChange = (slideIndex, shapeId, newText) => {
        setSlides(prevSlides => {
            const newSlides = [...prevSlides];
            const slideIndexInArray = newSlides.findIndex(s => s.index === slideIndex);
            if (slideIndexInArray === -1) return prevSlides;
            const slide = { ...newSlides[slideIndexInArray] };
            const shapeIndex = slide.shapes.findIndex(s => s.id === shapeId);
            if (shapeIndex === -1) return prevSlides;
            const newShapes = [...slide.shapes];
            newShapes[shapeIndex] = { ...newShapes[shapeIndex], text: newText };
            slide.shapes = newShapes;
            newSlides[slideIndexInArray] = slide;
            return newSlides;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const modifications = slides.map(slide => ({
                index: slide.index,
                shapes: slide.shapes
                    .filter(s => s.text !== s.originalText)
                    .map(s => ({ id: s.id, old_text: s.originalText, new_text: s.text }))
            })).filter(slide => slide.shapes.length > 0);

            if (modifications.length === 0) {
                alert("Aucune modification détectée.");
                setSaving(false); return;
            }
            const response = await api.post('/pptx/save', { blob_name: meta.blob_name, modifications: modifications });
            setMeta(prev => ({ ...prev, blob_name: response.data.new_blob_name }));
            setSlides(prev => prev.map(slide => ({ ...slide, shapes: slide.shapes.map(s => ({ ...s, originalText: s.text })) })));
            alert("Sauvegardé avec succès !");
        } catch (error) { console.error(error); alert("Erreur sauvegarde"); }
        finally { setSaving(false); }
    };

    // --- TELECHARGEMENT ---
    const handleDownload = async (blobNameOverride = null) => {
        const targetBlob = blobNameOverride || (meta && meta.blob_name);
        if (!targetBlob) return;

        try {
            const response = await api.get(`/download/${targetBlob}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', targetBlob);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Erreur téléchargement", err);
        }
    }

    // --- ENVOI EMAIL ---
    const handleSendEmail = async () => {
        if (!meta || !meta.blob_name) return;
        if (!window.confirm("Envoyer ce document à votre adresse email ?")) return;
        setSendingEmail(true);
        try {
            const response = await api.post('/pptx/email', { blob_name: meta.blob_name });
            alert(response.data.message || "Email envoyé avec succès !");
        } catch (error) {
            console.error("Erreur envoi email", error);
            alert(error.response?.data?.error || "Erreur lors de l'envoi.");
        } finally {
            setSendingEmail(false);
        }
    };

    const goToSlide = (index) => {
        setCurrentSlideIndex(Math.max(0, Math.min(slides.length - 1, index)));
    };

    const currentSlide = slides[currentSlideIndex];

    // --- EXTRACTION GLOBALE DES COULEURS DU THÈME ---
    const rawColors = slides[0]?.background
        ?.filter(bg => bg.type === 'color')
        .map(bg => bg.color) || [];

    // 🪄 MAGIE JAVASCRIPT : [...new Set(array)] supprime tous les doublons
    // [Bleu, Bleu Foncé, Bleu Foncé, Jaune] devient -> [Bleu, Bleu Foncé, Jaune]
    const globalColors = [...new Set(rawColors)];

    // Attribution des couleurs propres
    const primaryColor = globalColors[0] || '#6a9eb5';   // 1ère couleur unique
    const secondaryColor = globalColors[1] || '#1e4e79'; // 2ème couleur unique
    const accentColor = globalColors[2] || '#f5c04f';    // 3ème couleur unique (Enfin le Jaune !)

    return createPortal(
        <div className={styles.overlay}>
            {/* ══ HEADER ══ */}
            <header className={styles.header}>
                <div className={styles.headerTitleGroup}>
                    <span className={styles.headerIconBadge}>
                        <LayoutTemplate size={18} strokeWidth={2} />
                    </span>
                    <div className={styles.headerTitleText}>
                        <h3 className={styles.headerTitle}>Éditeur du Dossier</h3>
                        {fileName && <p className={styles.headerSubtitle}>{fileName}</p>}
                    </div>
                </div>

                {meta && (
                    <div className={styles.headerActions}>
                        <button
                            onClick={handleSendEmail}
                            disabled={sendingEmail || saving}
                            className={`${styles.actionBtn} ${styles.btnEmail}`}
                        >
                            <Mail size={15} strokeWidth={2} />
                            <span className={styles.actionBtnLabel}>{sendingEmail ? 'Envoi...' : 'Envoyer par mail'}</span>
                        </button>

                        <button
                            onClick={() => handleDownload()}
                            className={`${styles.actionBtn} ${styles.btnDownload}`}
                        >
                            <Download size={15} strokeWidth={2} />
                            <span className={styles.actionBtnLabel}>Télécharger</span>
                        </button>

                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className={`${styles.actionBtn} ${styles.btnSave}`}
                        >
                            <Save size={15} strokeWidth={2} />
                            <span className={styles.actionBtnLabel}>{saving ? '...' : 'Sauvegarder'}</span>
                        </button>

                        <span className={styles.headerDivider} />

                        <button onClick={onClose} className={styles.closeBtn} aria-label="Fermer l'éditeur">
                            <X size={20} strokeWidth={2} />
                        </button>
                    </div>
                )}
            </header>

            {/* ══ BANDEAU D'AVERTISSEMENT — RENDU APPROXIMATIF ══ */}
            {meta && (
                <div className={styles.disclaimerBanner}>
                    <Info size={14} strokeWidth={2} className={styles.disclaimerIcon} />
                    <span>
                        Aperçu approximatif : la mise en page, les polices et les positions peuvent
                        légèrement différer du fichier PowerPoint final.
                    </span>
                </div>
            )}

            {/* ══ SCÈNE PRINCIPALE ══ */}
            <div className={styles.stage} ref={stageRef}>

                {loading && (
                    <div className={styles.loaderWrap}>
                        <span className={styles.loader}></span>
                        <p className={styles.loaderText}>Chargement de l'édition...</p>
                    </div>
                )}

                {!loading && meta && currentSlide && (
                    <div className={styles.slideArea}>

                        <div
                            className={styles.slideCanvasWrapper}
                            style={{ width: meta.width * scale, height: meta.height * scale }}
                        >
                            <div
                                className={styles.slideCanvas}
                                style={{
                                    width: meta.width,
                                    height: meta.height,
                                    transform: `scale(${scale})`
                                }}
                            >
                                {/* 1. GABARIT FRONTEND SUR MESURE (Basé sur les vraies slides) */}
                                <div
                                    className={styles.templateBackground}
                                    style={{
                                        '--theme-primary': primaryColor,
                                        '--theme-secondary': secondaryColor,
                                        '--theme-accent': accentColor // Injection de la couleur dynamique
                                    }}
                                >
                                    {/* === SLIDE 1 : Page de garde === */}
                                    {currentSlide.index === 0 && (
                                        <>
                                            {/* Utilisation d'un SVG pour de vraies courbes fluides */}
                                            <svg
                                                className={styles.slide1_waves}
                                                viewBox="0 0 100 100"
                                                preserveAspectRatio="none"
                                            >
                                                {/* La vague foncée (Arrière-plan) */}
                                                <path
                                                    d="M 0,0 L 0,40 C 12,48 55,15 90,0 Z"
                                                    fill="var(--theme-secondary)"
                                                />
                                                {/* La vague claire (Premier plan) */}
                                                <path
                                                    d="M 0,0 L 0,30 C 9,36 40,11 78,0 Z"
                                                    fill="var(--theme-primary)"
                                                />
                                            </svg>

                                            {/* La fine ligne jaune sous le titre */}
                                            <div className={styles.slide1_yellowLine} />
                                        </>
                                    )}

                                    {/* === SLIDE 2 : Synthèse === */}
                                    {currentSlide.index === 1 && (
                                        <>
                                            {/* 1. La grande barre horizontale claire en arrière-plan */}
                                            <div className={styles.slide2_topBar} />

                                            {/* 2. Le trapèze d'en-tête foncé par-dessus */}
                                            <div className={styles.headerHexagon} />

                                            {/* 3. Les 3 rectangles de titres (Bleu foncé) */}
                                            <div className={styles.slide2_titleBlockProjets} />
                                            <div className={styles.slide2_titleBlockCompetences} />
                                            <div className={styles.slide2_titleBlockParcours} />

                                            {/* Petite ligne jaune en bas à droite pour le numéro de page */}
                                            <div className={styles.footerYellowLine} />
                                        </>
                                    )}

                                    {/* === SLIDE 3 et + : Missions / Détails === */}
                                    {currentSlide.index >= 2 && (
                                        <>
                                            {/* Même trapèze d'en-tête que la slide 2 */}
                                            <div className={styles.slide2_topBar} />
                                            <div className={styles.headerHexagon} />
                                            {/* La grande barre foncée transversale "REALISATIONS" */}
                                            <div className={styles.slide3_middleBar} />
                                            <div className={styles.footerYellowLine} />
                                        </>
                                    )}
                                </div>

                                {/* 2. LOGO (On ignore les blocs de couleur de l'API) */}
                                {currentSlide.background?.map((bg, idx) => {
                                    if (bg.type === 'logo') {
                                        return (
                                            <img
                                                key={`logo-${idx}`}
                                                src={bg.src}
                                                alt="Logo"
                                                style={{
                                                    position: 'absolute',
                                                    left: `${bg.x}px`,
                                                    top: `${bg.y}px`,
                                                    width: `${bg.width}px`,
                                                    height: `${bg.height}px`,
                                                    objectFit: 'contain',
                                                    pointerEvents: 'none',
                                                    zIndex: 5
                                                }}
                                            />
                                        );
                                    }
                                    return null;
                                })}

                                {/* 3. CHAMPS DE TEXTE */}
                                {currentSlide.shapes.map(shape => (
                                    <textarea
                                        key={shape.id}
                                        value={shape.text}
                                        onChange={(e) => handleTextChange(currentSlide.index, shape.id, e.target.value)}
                                        className={styles.shapeInput}
                                        style={{
                                            left: `${shape.x}px`,
                                            top: `${shape.y}px`,
                                            width: `${shape.width}px`,
                                            height: `${shape.height}px`
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* NAV PILL & THUMBSTRIP */}
                        <div className={styles.navPill}>
                            <button
                                className={styles.navArrow}
                                onClick={() => goToSlide(currentSlideIndex - 1)}
                                disabled={currentSlideIndex === 0}
                                aria-label="Diapositive précédente"
                            >
                                <ChevronLeft size={16} strokeWidth={2.5} />
                            </button>
                            <span className={styles.navCounter}>{currentSlideIndex + 1} / {slides.length}</span>
                            <button
                                className={styles.navArrow}
                                onClick={() => goToSlide(currentSlideIndex + 1)}
                                disabled={currentSlideIndex === slides.length - 1}
                                aria-label="Diapositive suivante"
                            >
                                <ChevronRight size={16} strokeWidth={2.5} />
                            </button>
                        </div>

                        {slides.length > 1 && (
                            <div className={styles.thumbStrip}>
                                {slides.map((slide, idx) => (
                                    <button
                                        key={slide.id ?? idx}
                                        className={`${styles.thumbChip} ${idx === currentSlideIndex ? styles.thumbChipActive : ''}`}
                                        onClick={() => goToSlide(idx)}
                                    >
                                        {idx + 1}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {!loading && meta && slides.length === 0 && (
                    <p className={styles.emptyStage}>Aucun contenu détecté dans ce document.</p>
                )}
            </div>
        </div>,
        document.body
    );
};

export default PptxEditor;